import { OptionsApi } from '@core/swagger-client';
import {
    ArrowRight,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessagePage,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Polls,
    Presentation,
    Survey,
    Users
} from '@core/ui';
import { Link, useParams, useRememberPage } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const WebinarInsights = () => {
    const { webinarId }: { webinarId: string } = useParams({ strict: false });
    const { t } = useTranslation();
    useRememberPage('insights', `/${webinarId}/insights`);

    const { data: webinar, isLoading, error: webinarError } = useQuery(OptionsApi.dashboard.getContentSummary(webinarId!));
    const {
        data: chatMessagesCount,
        isLoading: isChatMessagesCountLoading,
        error: chatMessagesCountError
    } = useQuery(OptionsApi.insights.getChatMessagesCount(webinarId!));
    const { data: survey, isLoading: isSurveyLoading, error: surveyError } = useQuery(OptionsApi.survey.get(webinarId!));
    const { data: exam, isLoading: isExamLoading, error: examError } = useQuery(OptionsApi.exam.get(webinarId!));

    const error = webinarError ?? chatMessagesCountError ?? surveyError ?? examError;
    if (error) return <ErrorMessagePage apiError={error} />;

    if (isLoading || isChatMessagesCountLoading || isSurveyLoading || isExamLoading || !webinar) return <LoaderPage />;

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>
                    <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                </PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="overview" />
                <ContentHeader contentHeader="Webinar Insights" testId="webinar-insights" />
                <ul className="max-w-300 grid sm:grid-cols-cards gap-4 md:gap-6">
                    <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                        <Presentation classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                        <p className="font-accent text-2xl mb-4">
                            <span className="block font-bold text-4xl md:text-5xl mb-1">{chatMessagesCount}</span>
                            {t('Insights.Chat.PageHeading')}
                        </p>
                        <Link to="/$webinarId/chat-messages" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                            {t('Generic.Link.ViewDetails')}
                            <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                        </Link>
                        <Link
                            to="/$webinarId/chat-messages"
                            params={{ webinarId }}
                            state={{ backTo: 'insights' }}
                            className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </li>
                    <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                        <Users classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                        <p className="font-accent text-2xl mb-4">
                            <span className="block font-bold text-4xl md:text-5xl mb-1">{webinar.numberOfInterested}</span>
                            {t('Insights.Interested.PageHeading')}
                        </p>
                        <Link to="/$webinarId/interested-accounts" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                            {t('Generic.Link.ViewDetails')}
                            <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                        </Link>
                        <Link
                            to="/$webinarId/interested-accounts"
                            params={{ webinarId }}
                            state={{ backTo: 'insights' }}
                            className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </li>
                    <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                        <Users classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                        <p className="font-accent text-2xl mb-4">
                            <span className="block font-bold text-4xl md:text-5xl mb-1">{webinar.numberOfLiveParticipants}</span>
                            {t('Insights.Live.PageHeading')}
                        </p>
                        <Link to="/$webinarId/live-participants" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                            {t('Generic.Link.ViewDetails')}
                            <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                        </Link>
                        <Link
                            to="/$webinarId/live-participants"
                            params={{ webinarId }}
                            state={{ backTo: 'insights' }}
                            className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </li>
                    <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                        <Users classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                        <p className="font-accent text-2xl mb-4">
                            <span className="block font-bold text-4xl md:text-5xl mb-1">{webinar.numberOfOnDemandParticipants}</span>
                            {t('Insights.OnDemand.PageHeading')}
                        </p>
                        <Link to="/$webinarId/on-demand-participants" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                            {t('Generic.Link.ViewDetails')}
                            <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                        </Link>
                        <Link
                            to="/$webinarId/on-demand-participants"
                            params={{ webinarId }}
                            state={{ backTo: 'insights' }}
                            className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </li>
                    <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                        <Polls classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                        <p className="font-accent text-2xl mb-4">
                            <span className="block font-bold text-4xl md:text-5xl mb-1">{webinar.numberOfPolls}</span>
                            {t('Insights.Polls.PageHeading')}
                        </p>
                        <Link to="/$webinarId/poll-result" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                            {t('Generic.Link.ViewDetails')}
                            <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                        </Link>
                        <Link
                            to="/$webinarId/poll-result"
                            params={{ webinarId }}
                            state={{ backTo: 'insights' }}
                            className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </li>
                    {survey?.enabled && (
                        <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                            <Survey classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                            <p className="font-accent text-2xl mb-4">
                                <span className="block font-bold text-4xl md:text-5xl mb-1">{survey.questions.length}</span>
                                {t('Insights.Survey.PageHeading')}
                            </p>
                            <Link to="/$webinarId/survey-result" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                                {t('Generic.Link.ViewDetails')}
                                <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                            </Link>
                            <Link
                                to="/$webinarId/survey-result"
                                params={{ webinarId }}
                                state={{ backTo: 'insights' }}
                                className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                                tabIndex={-1}
                                aria-hidden="true"
                            />
                        </li>
                    )}
                    {exam?.enabled && (
                        <li className="group relative bg-primary rounded-lg text-card-color p-4 text-center outline outline-primary outline-0 hover:outline-4 focus-within:outline-4 shadow-lg shadow-primary/0 hover:shadow-primary/40 focus-within:shadow-primary/40 transition-all ease-in-out duration-200 motion-reduce:duration-0">
                            <Survey classNames="h-14 w-auto mx-auto mt-2 mb-3 stroke-card-icon" />
                            <p className="font-accent text-2xl mb-4">
                                <span className="block font-bold text-4xl md:text-5xl mb-1">{exam.questions.length}</span>
                                {t('Insights.Exam.PageHeading')}
                            </p>
                            <Link to="/$webinarId/exam-result" params={{ webinarId }} state={{ backTo: 'insights' }} className="font-medium inline-flex items-center mt-auto mx-auto">
                                {t('Generic.Link.ViewDetails')}
                                <ArrowRight classNames="w-2.5 h-auto ml-2 stroke-current fill-transparent group-hover:fill-current" />
                            </Link>
                            <Link
                                to="/$webinarId/exam-result"
                                params={{ webinarId }}
                                state={{ backTo: 'insights' }}
                                className="absolute inset-0 rounded-lg overflow-hidden indent-[200%] whitespace-nowrap"
                                tabIndex={-1}
                                aria-hidden="true"
                            />
                        </li>
                    )}
                </ul>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
